import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SECTIONS = ["A", "B", "C", "D"];

export default function FineReport() {
  // Handler for downloading data as Excel
  const handleDownloadExcel = () => {
    if (!reportData.length) {
      alert("No data to export.");
      return;
    }
    const excelData = [];
    // Header
    excelData.push(["S. No.", "User ID", "Fine Amount"]);
    // Data rows
    reportData.forEach((row, idx) => {
      excelData.push([idx + 1, row.user_id, row.fine_amount]);
    });
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    ws["!cols"] = [{ wch: 8 }, { wch: 15 }, { wch: 15 }];
    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fine Report");
    // Generate filename with date range
    const fileName = `Fine_Report_${fromDate || "all"}_to_${toDate || "all"}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };
  const [programs, setPrograms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [breakupOpen, setBreakupOpen] = useState(false);
  const [breakupLoading, setBreakupLoading] = useState(false);
  const [breakupRows, setBreakupRows] = useState([]);
  const [breakupPageSize, setBreakupPageSize] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);

  // Get user info from session
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  useEffect(() => {
    if (clientId && collegeId) {
      setLoading(true);
      axios
        .get(`${API_BASE_URL}/api/finereport/programs/${clientId}/${collegeId}`)
        .then((res) => setPrograms(res.data.data || []))
        .finally(() => setLoading(false));
    }
  }, [clientId, collegeId]);

  useEffect(() => {
    if (selectedProgram && clientId && collegeId) {
      setLoading(true);
      axios
        .get(`${API_BASE_URL}/api/finereport/branches/${clientId}/${collegeId}/${selectedProgram}`)
        .then((res) => setBranches(res.data.data || []))
        .finally(() => setLoading(false));
      setSelectedBranch("");
      setBatches([]);
      setSelectedBatch("");
      setSelectedSection("");
    }
  }, [selectedProgram, clientId, collegeId]);

  useEffect(() => {
    if (selectedBranch && selectedProgram && clientId && collegeId) {
      setLoading(true);
      axios
        .get(
          `${API_BASE_URL}/api/finereport/batches/${clientId}/${collegeId}/${selectedProgram}/${selectedBranch}`
        )
        .then((res) => setBatches(res.data.data || []))
        .finally(() => setLoading(false));
      setSelectedBatch("");
      setSelectedSection("");
    }
  }, [selectedBranch, selectedProgram, clientId, collegeId]);

  const handleGetReport = () => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/finereport/report`, {
        params: {
          client_id: clientId,
          college_id: collegeId,
          program_id: selectedProgram,
          branch_id: selectedBranch,
          admittedbatch_id: selectedBatch,
          section: selectedSection,
          from_date: fromDate,
          to_date: toDate,
        },
      })
      .then((res) => {
        if (res.data && res.data.data) {
          setReportData(res.data.data.report || []);
        } else {
          setReportData([]);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleDownloadPDF = async () => {
    try {
      const params = {
        client_id: clientId,
        college_id: collegeId,
        program_id: selectedProgram,
        branch_id: selectedBranch,
        admittedbatch_id: selectedBatch,
        section: selectedSection,
        from_date: fromDate,
        to_date: toDate,
      };
      const response = await fetch(`${API_BASE_URL}/api/finereport/download-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/pdf" },
        body: JSON.stringify(params),
      });
      if (!response.ok) throw new Error("Failed to download PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "FineReport.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download PDF: " + err.message);
    }
  };

  const columns = [
    { field: "s_no", headerName: "S.No", width: 70 },
    { field: "user_id", headerName: "User ID", width: 120 },
    { field: "fine_amount", headerName: "Fine Amount", width: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Button
          onClick={() => handleOpenBreakup(params.row)}
          variant="text"
          size="medium"
          sx={{
            color: "#000",
            fontWeight: 600,
            textTransform: "none",
            background: "none",
            border: "none",
            boxShadow: "none",
            cursor: "pointer",
            p: 0,
            minWidth: 0,
            "&:hover": { background: "none", color: "#333" },
          }}
        >
          Fine Breakup
        </Button>
      ),
    },
  ];

  const handleOpenBreakup = (row) => {
    setSelectedUser(row);
    setBreakupOpen(true);
    setBreakupLoading(true);
    axios
      .get(`${API_BASE_URL}/api/finereport/fine-breakup`, {
        params: {
          client_id: clientId,
          college_id: collegeId,
          user_id: row.user_id,
          from_date: fromDate,
          to_date: toDate,
        },
      })
      .then((res) => {
        setBreakupRows(res.data.data || []);
      })
      .finally(() => setBreakupLoading(false));
  };

  const handleCloseBreakup = () => {
    setBreakupOpen(false);
    setBreakupRows([]);
    setSelectedUser(null);
  };

  // Fine breakup DataGrid columns
  const breakupColumns = [
    {
      field: "s_no",
      headerName: "S.No",
      width: 70,
      valueGetter: (params) =>
        params.api ? params.api.getRowIndex(params.id) + 1 : params.row.s_no || 1,
    },
    { field: "fine_amount", headerName: "Fine Amount", width: 120 },
    { field: "fine_reason", headerName: "Reason", width: 200 },
    {
      field: "fine_date",
      headerName: "Fine Date",
      width: 120,
      valueFormatter: (params) => (params.value ? params.value.substring(0, 10) : ""),
    },
    { field: "payment_status", headerName: "Payment Status", width: 140 },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar>
        <Typography variant="h6">Fine Report</Typography>
      </Toolbar>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        {/* ...existing filter fields... */}
        <TextField
          label="Program"
          name="program"
          select
          value={selectedProgram}
          onChange={(e) => setSelectedProgram(e.target.value)}
          size="medium"
          sx={{ minWidth: 180 }}
          disabled={false}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Program</option>
          {programs.map((p) => (
            <option key={p.program_id} value={p.program_id}>
              {p.program_name}
            </option>
          ))}
        </TextField>

        <TextField
          label="Branch"
          name="branch"
          select
          value={selectedBranch}
          onChange={(e) => setSelectedBranch(e.target.value)}
          size="medium"
          sx={{ minWidth: 180 }}
          disabled={!selectedProgram}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Branch</option>
          {branches.map((b) => (
            <option key={b.branch_id} value={b.branch_id}>
              {b.branch_name}
            </option>
          ))}
        </TextField>

        <TextField
          label="Batch"
          name="batch"
          select
          value={selectedBatch}
          onChange={(e) => setSelectedBatch(e.target.value)}
          size="medium"
          sx={{ minWidth: 180 }}
          disabled={!selectedBranch}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Batch</option>
          {batches.map((batch) => (
            <option key={batch.admittedbatch_id} value={batch.admittedbatch_id}>
              {batch.admittedbatch}
            </option>
          ))}
        </TextField>

        <TextField
          label="Section"
          name="section"
          select
          value={selectedSection}
          onChange={(e) => setSelectedSection(e.target.value)}
          size="medium"
          sx={{ minWidth: 100 }}
          disabled={!selectedBatch}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Section</option>
          {SECTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </TextField>

        <TextField
          label="From Date"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          size="medium"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />

        <TextField
          label="To Date"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          size="medium"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />

        <Button
          variant="contained"
          onClick={handleGetReport}
          disabled={loading}
          sx={{ color: "#fff", minWidth: 120 }}
        >
          {loading ? <CircularProgress size={24} /> : "Get Report"}
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={handleDownloadPDF}
          sx={{ color: "#fff", minWidth: 120 }}
        >
          Download PDF
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadExcel}
          sx={{ minWidth: 160, background: "#222", color: "#fff", ml: 1 }}
        >
          Download Excel
        </Button>
      </Box>

      <Box sx={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={reportData.map((row, idx) => ({ id: idx, ...row }))}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          loading={loading}
          getRowId={(row) => row.s_no || row.id}
          disableSelectionOnClick
        />
      </Box>

      {/* Fine Breakup Dialog */}
      <Dialog open={breakupOpen} onClose={handleCloseBreakup} maxWidth="md" fullWidth>
        <DialogTitle>Fine Breakup for User ID: {selectedUser?.user_id}</DialogTitle>
        <DialogContent>
          {breakupLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 120,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: 400, mt: 2 }}>
              <DataGrid
                rows={
                  Array.isArray(breakupRows)
                    ? breakupRows.map((row, idx) => ({ id: row.fine_id || idx, ...row }))
                    : []
                }
                columns={breakupColumns}
                loading={breakupLoading}
                pageSize={breakupPageSize}
                onPageSizeChange={(newSize) => setBreakupPageSize(newSize)}
                rowsPerPageOptions={[10, 20, 50]}
                disableSelectionOnClick
                autoHeight
                getRowId={(row) => row.fine_id || row.id}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBreakup}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
